import React, { useEffect, useState } from "react";

import { getFintoc } from "@fintoc/fintoc-js";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  AlertCircle,
  AlertTriangle,
  Building,
  ChevronDown,
  CreditCard,
  DollarSign,
  Download,
  Euro,
  Link2,
  Loader2,
  RefreshCw,
} from "lucide-react";
import * as XLSX from "xlsx";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { toast } from "~/hooks/use-toast";
import { trackEvent } from "~/integrations/posthog/client";
import { supabase } from "~/integrations/supabase/client";
import { cn } from "~/lib/utils";
import { BankLogo } from "~/pages/SuperAdminDashboard/BankLogo";
import { useProfileStore } from "~/store/profileStore";
import { formatCurrency } from "~/utils/helpers";

dayjs.extend(relativeTime);

const SUPABASE_FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL || "https://mdrvhekhimhcwydrpueo.supabase.co"}/functions/v1`;
const SUPABASE_ANON_KEY =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1kcnZoZWtoaW1oY3d5ZHJwdWVvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM1MzcyOTksImV4cCI6MjA1OTExMzI5OX0.XYVbRbA5VhJKv8p5Fe64oXfzeaoY7fMVPDfJlNXqElY";
const FINTOC_PUBLIC_KEY = import.meta.env.VITE_FINTOC_PUBLIC_KEY || "";
// An account whose data is older than this is considered broken: Rail
// auto-syncs every ~4h per link, so 72h without a refresh means the link
// stopped working (a credential problem Rail hasn't flagged on link.status yet).
const STALE_ACCOUNT_HOURS = 72;
const BACKFILL_DAYS = 90;
const BACKFILL_RETRY_DELAY_MS = 3 * 60 * 1000;

const functionAuthHeaders = {
  "Content-Type": "application/json",
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

interface BankAccount {
  id: string;
  name: string;
  official_name: string;
  number: string;
  type: string;
  balance_available: number | null;
  balance_current: number | null;
  balance_limit: number | null;
  holder_id: string;
  holder_name: string;
  currency: string;
  refreshed_at: string;
  object: string;
  rail_bank_id: string;
}

interface InstitutionMaintenance {
  starts_at?: string | null;
  ends_at?: string | null;
  note?: string | null;
}

interface Bank {
  id: string;
  institution_name: string;
  institution_id: string;
  institution_country: string;
  status: string | null;
  refresh_status: string | null;
  active: boolean | null;
  // Bank availability (Rail link.institution.status), fed by the rail-webhook
  // institution.* events. Distinct from `status` (link credential health).
  institution_status: string | null;
  institution_resumes_at: string | null;
  institution_maintenance: InstitutionMaintenance | null;
}

const BANKS_SELECT =
  "id, institution_name, institution_id, institution_country, status, refresh_status, active, institution_status, institution_resumes_at, institution_maintenance";

interface LastTransaction {
  date: string;
  description: string;
  amount: number;
}

// Rail bank-availability labels (link.institution.status), surfaced by the
// rail-webhook institution.* events. A bank-side outage/maintenance — NOT a
// credential problem (link.status stays "active"), so no user action is needed.
const INSTITUTION_STATUS_LABELS: Record<string, string> = {
  paused: "servicio pausado",
  maintenance: "en mantenimiento",
  disabled: "servicio deshabilitado",
};


interface AccountSummaryCardProps {
  showDetailedView: boolean;
  setShowDetailedView: (value: boolean) => void;
  selectedAccountId: string | null;
  handleAccountSelect: (accountId: string) => void;
}

const AccountSummaryCard: React.FC<AccountSummaryCardProps> = ({
  selectedAccountId,
  handleAccountSelect,
}) => {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [banks, setBanks] = useState<Record<string, Bank>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastTransactions, setLastTransactions] = useState<
    Record<string, LastTransaction | null>
  >({});
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [reconnectingAccountId, setReconnectingAccountId] = useState<
    string | null
  >(null);
  const [downloadingAccountId, setDownloadingAccountId] = useState<
    string | null
  >(null);
  const { role } = useProfileStore();
  // CLP (primary currency) starts expanded; USD/EUR start collapsed
  const [expandedCurrencies, setExpandedCurrencies] = useState<
    Record<string, boolean>
  >({ CLP: true });

  const toggleCurrency = (currency: string) =>
    setExpandedCurrencies((prev) => ({
      ...prev,
      [currency]: !prev[currency],
    }));

  // Group accounts by currency and calculate totals for each currency
  const currencyTotals = bankAccounts.reduce(
    (totals, account) => {
      const currency = account.currency || "CLP";
      const balance = account.balance_current ?? account.balance_available ?? 0;

      if (!totals[currency]) {
        totals[currency] = 0;
      }

      totals[currency] += balance;
      return totals;
    },
    {} as Record<string, number>
  );

  // Define currency order: CLP, USD, EUR, then any others
  const currencyOrder = ["CLP", "USD", "EUR"];
  const sortedCurrencies = Object.keys(currencyTotals).sort((a, b) => {
    const indexA = currencyOrder.indexOf(a);
    const indexB = currencyOrder.indexOf(b);

    // If both currencies are in the order array, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the order array, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in the order array, sort alphabetically
    return a.localeCompare(b);
  });

  // Get icon for currency
  const getCurrencyIcon = (currency: string) => {
    switch (currency) {
      case "USD":
        return <DollarSign className="h-4 w-4" />;
      case "EUR":
        return <Euro className="h-4 w-4" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  // Function to fetch the last transaction for each account
  const fetchLastTransactions = async (accountIds: string[]) => {
    try {
      const transactions: Record<string, LastTransaction | null> = {};

      for (const accountId of accountIds) {
        const { data, error } = await supabase
          .from("rail_movements")
          .select("id, transaction_date, description, amount")
          .eq("account_id", accountId)
          .gt("post_date", "2026-08-06")
          .order("transaction_date", { ascending: false })
          .limit(1);

        if (error) throw error;

        if (data && data.length > 0) {
          transactions[accountId] = {
            date: data[0].transaction_date,
            description: data[0].description,
            amount: data[0].amount,
          };
        } else {
          transactions[accountId] = null;
        }
      }

      setLastTransactions(transactions);
    } catch (error) {
      console.error("Error fetching last transactions:", error);
    }
  };

  // Fetch bank accounts and banks from Supabase
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch bank information first
        const { data: banksData, error: banksError } = await supabase
          .from("rail_banks")
          .select(BANKS_SELECT);

        if (banksError) throw banksError;

        // Create a map of bank IDs to bank objects
        const banksMap: Record<string, Bank> = {};
        if (banksData) {
          banksData.forEach((bank: Bank) => {
            banksMap[bank.id] = bank;
          });
          setBanks(banksMap);
        }

        // Then fetch the accounts
        const { data: accountsData, error: accountsError } = await supabase
          .from("rail_bank_accounts")
          .select("*");

        if (accountsError) throw accountsError;

        if (accountsData) {
          setBankAccounts(accountsData);

          // Fetch last transaction for each account
          fetchLastTransactions(accountsData.map((account) => account.id));
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch bank accounts";
        console.error("Error fetching bank data:", error);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Function to refresh account data
  const handleRefresh = async () => {
    setRefreshing(true);
    setLoadingMessage("Actualizando movimientos...");

    // Track the refresh action
    trackEvent("bank_accounts_refresh_clicked", {
      account_count: bankAccounts.length,
      currency_count: Object.keys(currencyTotals).length,
    });

    try {
      // Ask Rail to pull fresh data from the banks on demand (refresh intents);
      // the result flows back via rail-webhook a few seconds later. Fire-and-
      // forget — we still show whatever Rail already has below.
      axios
        .get(`${SUPABASE_FUNCTIONS_URL}/rail-refresh`, {
          headers: functionAuthHeaders,
        })
        .catch(() => {});

      // Refresh link status/balances first, then pull the latest movements.
      setLoadingMessage("Actualizando estado de bancos...");
      await axios.get(`${SUPABASE_FUNCTIONS_URL}/rail-fetch-accounts`, {
        headers: functionAuthHeaders,
      });

      setLoadingMessage("Actualizando movimientos...");
      const response = await axios.get(
        `${SUPABASE_FUNCTIONS_URL}/rail-fetch-movements`,
        { headers: functionAuthHeaders }
      );

      const result = response.data;
      console.log("Movements updated:", result);

      // Refresh bank information first
      const { data: banksData, error: banksError } = await supabase
        .from("rail_banks")
        .select(BANKS_SELECT);

      if (banksError) throw banksError;

      // Create a map of bank IDs to bank objects
      const banksMap: Record<string, Bank> = {};
      if (banksData) {
        banksData.forEach((bank: Bank) => {
          banksMap[bank.id] = bank;
        });
        setBanks(banksMap);
      }

      // Then refresh the accounts
      const { data: accountsData, error: accountsError } = await supabase
        .from("rail_bank_accounts")
        .select("*");

      if (accountsError) throw accountsError;

      if (accountsData) {
        setBankAccounts(accountsData);
        fetchLastTransactions(accountsData.map((account) => account.id));
      }

      toast({
        title: "Movimientos actualizados",
        description: `Se han actualizado ${result.movements || 0} movimientos bancarios.`,
      });

      // Track successful refresh
      trackEvent("bank_accounts_refresh_completed", {
        movements_processed: result.movements || 0,
        account_count: accountsData?.length || 0,
      });
    } catch (error) {
      console.error("Error refreshing bank accounts:", error);
      toast({
        title: "Error al actualizar movimientos",
        description:
          error instanceof Error ? error.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
      setIsLoading(false);
      setLoadingMessage(null);
    }
  };

  // Helper function to get bank name
  const getBankName = (bankId: string): string => {
    if (banks[bankId]) {
      return banks[bankId].institution_name;
    }
    return "Banco";
  };

  // Bank-side availability problem reported by Rail (institution.status_changed /
  // institution.maintenance_scheduled), stored on rail_banks.institution_status.
  // NOT a credential problem — link.status stays "active" during a bank outage.
  const getInstitutionIssue = (account: BankAccount): string | null => {
    const status = banks[account.rail_bank_id]?.institution_status;
    if (!status || status === "available") return null;
    return INSTITUTION_STATUS_LABELS[status] || status;
  };

  // A link needs reconnection when Rail reports a credential problem (link.status
  // failed | credentials_invalid | mfa_required | expired) or when the account
  // data went stale. A stale account during a bank-side outage is NOT flagged:
  // the credentials are fine, the bank itself is unreachable.
  const needsReconnection = (account: BankAccount): boolean => {
    const bank = banks[account.rail_bank_id];
    if (bank?.status && bank.status !== "active") return true;
    if (getInstitutionIssue(account)) return false;
    if (bank?.refresh_status === "interrupted") return true;
    return (
      !!account.refreshed_at &&
      dayjs().diff(dayjs(account.refreshed_at), "hour") > STALE_ACCOUNT_HOURS
    );
  };

  // Banks Rail currently reports as unavailable (outage / maintenance).
  const affectedInstitutions = Object.values(banks).filter(
    (bank) => bank.institution_status && bank.institution_status !== "available"
  );
  const showStatusAlert = affectedInstitutions.length > 0;

  // Pull movement history for one account (paginated) and let the edge
  // function reconcile duplicates left behind by a link replacement.
  const runBackfill = async (accountId: string) => {
    const since = dayjs().subtract(BACKFILL_DAYS, "day").format("YYYY-MM-DD");
    const response = await axios.get(
      `${SUPABASE_FUNCTIONS_URL}/rail-fetch-movements`,
      {
        params: { account_id: accountId, since, max_pages: 20 },
        headers: functionAuthHeaders,
      }
    );
    return response.data;
  };

  const completeReconnection = async (
    exchangeToken: string,
    account: BankAccount
  ) => {
    const bankName = getBankName(account.rail_bank_id);

    const { data: exchangeData, error: exchangeError } =
      await supabase.functions.invoke("reconnect-fintoc-link", {
        body: {
          action: "exchange",
          exchangeToken,
          replacesBankId: account.rail_bank_id,
        },
      });

    if (exchangeError || exchangeData?.error) {
      throw new Error(
        exchangeError?.message || exchangeData?.error || "Error desconocido"
      );
    }

    trackEvent("bank_link_reconnected", {
      bank_name: bankName,
      new_link_id: exchangeData.newLinkId,
      migrated_movements: exchangeData.migratedMovements,
    });

    toast({
      title: "Banco reconectado",
      description: `${bankName}: se migraron ${exchangeData.migratedMovements ?? 0} movimientos históricos. La primera sincronización puede tardar unos minutos.`,
    });

    const newAccounts: { id: string }[] = exchangeData.accounts || [];

    // Fintoc needs a few minutes for the first pull from the bank, so run one
    // backfill now and another after a delay to fill the outage gap.
    for (const newAccount of newAccounts) {
      runBackfill(newAccount.id).catch((backfillError) =>
        console.error("Initial backfill failed:", backfillError)
      );
      window.setTimeout(() => {
        runBackfill(newAccount.id)
          .then((result) =>
            toast({
              title: "Historial sincronizado",
              description: `${bankName}: se procesaron ${result?.movements ?? 0} movimientos.`,
            })
          )
          .catch((backfillError) =>
            console.error("Delayed backfill failed:", backfillError)
          );
      }, BACKFILL_RETRY_DELAY_MS);
    }

    await handleRefresh();
  };

  // NOTE: reconnection still uses the Fintoc widget (reconnect-fintoc-link). The
  // Rail Connect widget is a deferred phase-2 item (see docs/integrations/rail-banking.md).
  // The "Requiere reconexión" badge above is still valuable as an alert — meanwhile a
  // Rail link is reconnected from Rail's dashboard.
  const handleReconnect = async (account: BankAccount) => {
    setReconnectingAccountId(account.id);
    try {
      const { data: intentData, error: intentError } =
        await supabase.functions.invoke("reconnect-fintoc-link", {
          body: { action: "create_intent" },
        });

      if (intentError || !intentData?.widgetToken) {
        throw new Error(
          intentError?.message ||
            intentData?.error ||
            "No se pudo iniciar la conexión con Fintoc"
        );
      }

      // The edge function returns the Fintoc public key (safe for the
      // browser); the VITE_ env var is only a local override.
      const publicKey = FINTOC_PUBLIC_KEY || intentData.publicKey;
      if (!publicKey) {
        throw new Error(
          "Falta la llave pública de Fintoc (secret FINTOC_PUBLIC_API_PROD_KEY)"
        );
      }

      const Fintoc = await getFintoc();
      if (!Fintoc) {
        throw new Error("No se pudo cargar el widget de Fintoc");
      }

      const widget = Fintoc.create({
        publicKey,
        widgetToken: intentData.widgetToken,
        onSuccess: (linkIntent: {
          exchangeToken?: string;
          exchange_token?: string;
        }) => {
          const exchangeToken =
            linkIntent?.exchangeToken ?? linkIntent?.exchange_token;
          if (!exchangeToken) {
            toast({
              title: "Error al reconectar",
              description: "Fintoc no devolvió un token de intercambio.",
              variant: "destructive",
            });
            setReconnectingAccountId(null);
            return;
          }
          completeReconnection(exchangeToken, account)
            .catch((reconnectError) => {
              console.error("Error completing reconnection:", reconnectError);
              toast({
                title: "Error al reconectar",
                description:
                  reconnectError instanceof Error
                    ? reconnectError.message
                    : "Error desconocido",
                variant: "destructive",
              });
            })
            .finally(() => setReconnectingAccountId(null));
        },
        onExit: () => setReconnectingAccountId(null),
      });

      widget.open();
    } catch (reconnectError) {
      console.error("Error starting reconnection:", reconnectError);
      toast({
        title: "Error al reconectar",
        description:
          reconnectError instanceof Error
            ? reconnectError.message
            : "Error desconocido",
        variant: "destructive",
      });
      setReconnectingAccountId(null);
    }
  };

  // Function to download fintoc movements report for a bank account
  const downloadMovementsReport = async (account: BankAccount) => {
    setDownloadingAccountId(account.id);
    try {
      toast({
        title: "Descargando reporte",
        description: `Generando reporte de movimientos para ${account.official_name || account.name}...`,
      });

      // Use database function to get movements with factura associations
      const { data: movements, error } = await supabase.rpc(
        "get_bank_movements_with_facturas",
        {
          bank_account_id_param: account.id,
        }
      );

      if (error) {
        console.error("Error fetching movements with facturas:", error);
        throw error;
      }

      if (!movements || movements.length === 0) {
        toast({
          title: "Sin movimientos",
          description: "No se encontraron movimientos para esta cuenta.",
          variant: "destructive",
        });
        return;
      }

      console.log(
        `Found ${(movements as any[]).length} movements for account ${account.number}`
      );
      console.log("Raw movements response:", movements);
      console.log(
        "Sample movements with facturas:",
        (movements as any[]).slice(0, 5).map((m) => ({
          date: m.transaction_date || m.post_date,
          description: m.description,
          amount: m.amount,
          facturas: m.factura_numbers,
        }))
      );

      // Let's also test if there are any sales_request associations at all
      const { data: testMovements } = await supabase
        .from("rail_movements")
        .select("id, sales_request, description")
        .eq("account_id", account.id)
        .not("sales_request", "is", null)
        .limit(10);

      console.log("Movements with sales_request (raw):", testMovements);

      if (testMovements && testMovements.length > 0) {
        const { data: testDocs } = await supabase
          .from("sales_request_documents")
          .select("sales_request_id, bsale_number, document_type")
          .in(
            "sales_request_id",
            testMovements.map((m) => m.sales_request)
          )
          .not("bsale_number", "is", null);

        console.log("Matching factura documents:", testDocs);
      }

      // Create Excel workbook
      const workbook = XLSX.utils.book_new();

      // Prepare data for Excel
      const excelData = [
        ["Fecha", "Descripción", "Monto", "Referencia", "Número Factura"], // Headers
        ...(movements as any[]).map((movement: any) => {
          // Factura numbers are already concatenated by the database function
          const facturaNumber = movement.factura_numbers || "N/A";

          // Use transaction_date or post_date, and format to date only
          const dateValue = movement.transaction_date || movement.post_date;
          const formattedDate = dateValue
            ? new Date(dateValue).toLocaleDateString("es-CL")
            : "";

          return [
            formattedDate,
            movement.description || "",
            movement.amount || 0,
            movement.reference_id || "",
            facturaNumber,
          ];
        }),
      ];

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 12 }, // Fecha
        { wch: 40 }, // Descripción
        { wch: 15 }, // Monto
        { wch: 20 }, // Referencia
        { wch: 18 }, // Número Factura
      ];
      worksheet["!cols"] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Movimientos");

      // Generate filename
      const bankName = getBankName(account.rail_bank_id);
      const fileName = `movimientos_${bankName}_${account.number}_${new Date().toISOString().split("T")[0]}.xlsx`;

      // Write and download Excel file
      XLSX.writeFile(workbook, fileName);

      toast({
        title: "Reporte descargado",
        description: `Se descargó el reporte con ${(movements as any[]).length} movimientos.`,
      });

      trackEvent("rail_movements_report_downloaded", {
        account_id: account.id,
        bank_name: bankName,
        movements_count: (movements as any[]).length,
        currency: account.currency,
        format: "xlsx",
      });
    } catch (error) {
      console.error("Error downloading movements report:", error);
      toast({
        title: "Error al descargar",
        description: "No se pudo generar el reporte. Intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setDownloadingAccountId(null);
    }
  };

  if (isLoading && bankAccounts.length === 0) {
    return (
      <Card className="overflow-hidden rounded-xl border-none shadow-md">
        <CardHeader className="pb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-xl font-bold">
              <Building className="mr-2 h-5 w-5" />
              Cuentas Bancarias
            </CardTitle>
            <Skeleton className="h-9 w-24" />
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4">
            <Skeleton className="h-20 w-full rounded-lg" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="overflow-hidden rounded-xl border-none shadow-md">
        <CardHeader className="pb-0">
          <CardTitle className="flex items-center text-xl font-bold">
            <Building className="mr-2 h-5 w-5" />
            Cuentas Bancarias
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <AlertCircle className="mb-3 h-12 w-12 text-destructive" />
            <p className="text-lg font-medium text-destructive">
              Error cargando datos
            </p>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              {error}
            </p>
            <Button
              variant="default"
              size="sm"
              className="mt-4"
              onClick={handleRefresh}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="overflow-hidden rounded-xl border-none pb-4 shadow-md">
        <CardHeader className="bg-white px-2 pb-2 dark:bg-slate-900">
          <div className="flex w-full flex-row items-center justify-between">
            <CardTitle className="flex items-center text-xl font-bold">
              <Building className="mr-2 h-5 w-5" />
              Cuentas Bancarias
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 border-gray-300"
            >
              <RefreshCw
                className={cn(
                  "h-4 w-4",
                  refreshing && "animate-spin text-muted-foreground"
                )}
              />
              {refreshing ? "Actualizando..." : "Refrescar"}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="overflow-hidden !p-0 pt-4">
          {showStatusAlert && (
            <Alert className="mx-4 mb-2 mt-4 w-auto border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 !text-amber-600" />
              <AlertTitle className="text-amber-900">
                Incidencias en el servicio del banco
              </AlertTitle>
              <AlertDescription className="text-amber-800">
                <div className="space-y-1">
                  {affectedInstitutions.map((bank) => {
                    const label =
                      INSTITUTION_STATUS_LABELS[bank.institution_status!] ||
                      bank.institution_status;
                    const note = bank.institution_maintenance?.note;
                    const resumes = bank.institution_resumes_at
                      ? ` — se reanuda ${dayjs(bank.institution_resumes_at)
                          .locale("es")
                          .fromNow()}`
                      : "";
                    return (
                      <div key={bank.id}>
                        {bank.institution_name}: {label}
                        {note ? ` — ${note}` : ""}
                        {resumes}
                      </div>
                    );
                  })}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Simplified currency totals */}
          <div className="mb-6 grid grid-cols-1 gap-4 p-4 lg:grid-cols-2 xl:grid-cols-3">
            {sortedCurrencies.map((currency, index) => {
              const total = currencyTotals[currency];
              return (
                <Card
                  key={currency}
                  style={{ animationDelay: `${index * 75}ms` }}
                  className="w-full border border-gray-300 bg-white shadow-sm duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both dark:bg-slate-900"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-muted-foreground">
                          Balance Total
                        </div>
                        <div className="text-2xl font-bold">
                          {formatCurrency(total, currency)}
                        </div>
                      </div>
                      <Badge variant="outline" className="font-bold">
                        {getCurrencyIcon(currency)}
                        <span className="ml-1">{currency}</span>
                      </Badge>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      {
                        bankAccounts.filter((a) => a.currency === currency)
                          .length
                      }{" "}
                      cuentas
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Simplified accounts table by currency */}
          <div className="space-y-6">
            {sortedCurrencies.map((currency) => {
              const isExpanded = !!expandedCurrencies[currency];
              const accountCount = bankAccounts.filter(
                (account) => account.currency === currency
              ).length;

              return (
                <div key={currency} className="px-4">
                  <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm dark:bg-slate-900">
                    <button
                      type="button"
                      onClick={() => toggleCurrency(currency)}
                      aria-expanded={isExpanded}
                      className={cn(
                        "flex w-full items-center justify-between bg-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-200/60",
                        isExpanded && "border-b border-gray-300"
                      )}
                    >
                      <div className="flex items-center">
                        <Badge
                          className="mr-2 border-gray-300 bg-white"
                          variant="outline"
                        >
                          {getCurrencyIcon(currency)}
                          <span className="ml-1">{currency}</span>
                        </Badge>
                        <h3 className="text-lg font-medium">Cuentas</h3>
                        <span className="ml-2 text-sm text-muted-foreground">
                          ({accountCount})
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <span className="hidden sm:inline">
                          {isExpanded ? "Ocultar" : "Mostrar"}
                        </span>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform duration-200",
                            isExpanded && "rotate-180"
                          )}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="relative w-full duration-200 animate-in fade-in-0 slide-in-from-top-1">
                        <div className="overflow-x-auto whitespace-nowrap">
                          <div className="max-w-full">
                            <table className="w-full min-w-[800px] table-auto border-collapse">
                              <thead className="bg-gray-100 dark:bg-slate-900">
                                <tr>
                                  <th className="min-w-[300px] border px-4 py-2 text-left font-medium">
                                    Cuenta
                                  </th>
                                  <th className="min-w-[150px] border px-4 py-2 text-center font-medium">
                                    Número
                                  </th>
                                  <th className="min-w-[150px] border px-4 py-2 text-center font-medium">
                                    Institución
                                  </th>
                                  <th className="min-w-[150px] border px-4 py-2 text-right font-medium">
                                    Balance
                                  </th>
                                  <th className="min-w-[120px] border px-4 py-2 text-center font-medium">
                                    Acciones
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {bankAccounts
                                  .filter(
                                    (account) => account.currency === currency
                                  )
                                  .map((account, index) => {
                                    const balance =
                                      account.balance_current ??
                                      account.balance_available ??
                                      0;
                                    const reconnectNeeded =
                                      needsReconnection(account);
                                    const institutionIssue =
                                      getInstitutionIssue(account);

                                    return (
                                      <tr
                                        key={account.id}
                                        style={{
                                          animationDelay: `${index * 60}ms`,
                                        }}
                                        className={`cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-slate-50 dark:hover:bg-slate-800 ${
                                          selectedAccountId === account.id
                                            ? "bg-slate-100 dark:bg-slate-800"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          trackEvent("bank_account_selected", {
                                            account_id: account.id,
                                            bank_name: getBankName(
                                              account.rail_bank_id
                                            ),
                                            currency: account.currency,
                                            balance:
                                              account.balance_current ??
                                              account.balance_available ??
                                              0,
                                          });
                                          handleAccountSelect(account.id);
                                        }}
                                      >
                                        <td className="border px-4 py-2">
                                          <div className="flex items-center">
                                            <div className="mr-2 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                                              <CreditCard className="h-4 w-4" />
                                            </div>
                                            <div>
                                              <div className="font-medium">
                                                {account.official_name ||
                                                  account.name}
                                              </div>
                                              <div className="text-xs text-muted-foreground">
                                                {account.holder_name}
                                              </div>
                                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                                {reconnectNeeded && (
                                                  <Badge variant="destructive">
                                                    Requiere reconexión
                                                  </Badge>
                                                )}
                                                {institutionIssue && (
                                                  <Badge className="border-amber-300 bg-amber-100 capitalize text-amber-800 hover:bg-amber-100">
                                                    {institutionIssue}
                                                  </Badge>
                                                )}
                                                {account.refreshed_at && (
                                                  <span className="text-xs text-muted-foreground">
                                                    Actualizado{" "}
                                                    {dayjs(account.refreshed_at)
                                                      .locale("es")
                                                      .fromNow()}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="border px-4 py-2 text-center font-mono text-sm">
                                          {account.number}
                                        </td>
                                        <td className="border px-4 py-2 text-center">
                                          <div className="flex items-center justify-center gap-2">
                                            <BankLogo
                                              institutionId={
                                                banks[account.rail_bank_id]
                                                  ?.institution_id
                                              }
                                              name={getBankName(
                                                account.rail_bank_id
                                              )}
                                            />
                                            <span className="text-sm font-medium">
                                              {getBankName(
                                                account.rail_bank_id
                                              )}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="border px-4 py-2 text-right font-medium">
                                          {formatCurrency(balance, currency)}
                                          {account.balance_available !== null &&
                                            account.balance_available !==
                                              account.balance_current && (
                                              <div className="text-xs text-muted-foreground">
                                                Disponible:{" "}
                                                {formatCurrency(
                                                  account.balance_available,
                                                  currency
                                                )}
                                              </div>
                                            )}
                                        </td>
                                        <td className="border px-4 py-2 text-center">
                                          <div className="flex items-center justify-center gap-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                void downloadMovementsReport(
                                                  account
                                                );
                                              }}
                                              disabled={
                                                downloadingAccountId ===
                                                account.id
                                              }
                                              className="flex items-center gap-1 border-gray-300"
                                            >
                                              {downloadingAccountId ===
                                              account.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                              ) : (
                                                <Download className="h-4 w-4" />
                                              )}
                                              {downloadingAccountId ===
                                              account.id
                                                ? "Descargando..."
                                                : "Descargar"}
                                            </Button>
                                            {reconnectNeeded &&
                                              role === "superadmin" && (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  disabled={
                                                    reconnectingAccountId ===
                                                    account.id
                                                  }
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleReconnect(account);
                                                  }}
                                                  className="flex items-center gap-1 border-gray-300"
                                                >
                                                  {reconnectingAccountId ===
                                                  account.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                  ) : (
                                                    <Link2 className="h-4 w-4" />
                                                  )}
                                                  Reconectar
                                                </Button>
                                              )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default AccountSummaryCard;
